import type { DbExecutor } from "./executor";
import type { CatalogRepository } from "./repositories/catalogRepository";
import type { AssemblyRepository } from "./repositories/assemblyRepository";
import type { BomService } from "./bomService";
import { ValidationError } from "./repositories/projectRepository";
import {
  buildSearchQuery,
  coverageFromPackageFields,
  displayProductName,
  parseSearchStoreContext,
  coverageFromSpecifications,
  homeDepotProductUrl,
  parseProductResponse,
  parseSearchResponse,
  providerCacheKey,
  type CoverageFinding,
  type HomeDepotProductDetail,
  type HomeDepotSearchResult,
} from "../sourcing/homeDepot";
import { ProviderError, type ProviderExecutor } from "../sourcing/providerExecutor";
import type { HomeDepotStore } from "../sourcing/homeDepotStores";

/*
 * The store index is a 107 kB table only a product search or the store
 * picker ever reads (spec §88B): it was the largest single module in the
 * first bundle every customer downloads. It loads the first time it is
 * needed; the lookups themselves are unchanged.
 */
const storeIndex = () => import("../sourcing/homeDepotStores");
import { objectSearchAttributes, type SearchAttribute } from "../domain/objectSearch";
import type { QuantityUnit } from "../domain/feature";
import type { Product } from "../domain/catalog";

/**
 * Live product sourcing (Phase 9B.1): discovery through a real provider,
 * mapped into the EXISTING catalog/offer/selection architecture.
 *
 * Rules preserved:
 *  - the API credential lives in the transport (Rust today, a server
 *    endpoint under SaaS) — this service only receives JSON;
 *  - provider results are DISCOVERY, not truth about compatibility: they
 *    are searched within one material requirement's context and the user
 *    chooses;
 *  - selecting a result writes a provider-provenanced Offer and a
 *    copy-on-use project snapshot — later offer changes still require an
 *    explicit Keep/Update;
 *  - a provider product seen twice reuses ONE Product row (external
 *    identity), so the catalog never fills up with duplicates;
 *  - nothing is invented: a field the provider did not return stays empty
 *    and is reported as missing.
 */

export const HOME_DEPOT_PROVIDER_ID = "home-depot";
export const HOME_DEPOT_SUPPLIER_NAME = "Home Depot";

/** How long a cached provider response stays useful (quota protection). */
export const PROVIDER_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

export interface SearchOptions {
  query: string;
  zip: string;
  /** Resolves the project's store (override, else the ZIP's store). */
  projectId?: string | null;
  storeId?: string | null;
  /** Ignore the local cache for this one request (explicit user refresh). */
  forceRefresh?: boolean;
  page?: number;
}

export interface SearchOutcome {
  results: HomeDepotSearchResult[];
  /** True when served from the local cache (no API call was made). */
  fromCache: boolean;
  fetchedAt: string;
  query: string;
  zip: string;
  /** The store the PROVIDER actually priced against — always displayed. */
  storeId: string | null;
  storeName: string | null;
  /** The store we asked for, resolved from the project's ZIP or override. */
  requestedStore: HomeDepotStore | null;
  requestedStoreMatch: "exact" | "nearby" | "override" | null;
}

export interface DetailOutcome {
  detail: HomeDepotProductDetail | null;
  coverage: CoverageFinding | null;
  fromCache: boolean;
  fetchedAt: string;
}

export interface SelectResultInput {
  projectId: string;
  /** Requirement components this selection applies to. */
  componentIds: string[];
  materialDefinitionId: string;
  result: HomeDepotSearchResult;
  /** Detail (when the user expanded it) — better identity + coverage. */
  detail?: HomeDepotProductDetail | null;
  coverage?: CoverageFinding | null;
  zip: string;
  /** Only needed when the provider gave no package coverage. */
  manualCoverage?: { coveragePerUnitX100: number; coverageUnit: QuantityUnit; purchaseUnit: QuantityUnit } | null;
  /** Store the provider priced against (recorded on the offer). */
  storeName?: string | null;
}

export interface SelectResultOutcome {
  productId: string;
  productCreated: boolean;
  offerId: string;
  /** Set when the requirement needs a package figure the provider lacked. */
  missingCoverage: boolean;
  productUrl: string | null;
}

/**
 * Package coverage precedence: the provider's structured package fields
 * first (authoritative), then a coverage specification, then nothing — in
 * which case the app asks for that ONE figure.
 */
function coverageFor(detail: HomeDepotProductDetail | null): CoverageFinding | null {
  if (!detail) return null;
  return coverageFromPackageFields(detail) ?? coverageFromSpecifications(detail.specifications);
}

interface CacheRow {
  response: string;
  fetched_at: string;
}

export class ProviderSourcingService {
  constructor(
    private readonly db: DbExecutor,
    private readonly executor: ProviderExecutor,
    private readonly catalog: CatalogRepository,
    private readonly assemblies: AssemblyRepository,
    private readonly bom: BomService,
    private readonly now: () => Date = () => new Date(),
  ) {}

  async isConfigured(): Promise<{ configured: boolean; path: string }> {
    return this.executor.credentialStatus();
  }

  /**
   * Default query text for a requirement — deterministic, user-editable.
   *
   * When the requirement traces back to a placed object that KNOWS what it
   * is (spec §61K), its structured size and type join the query: "30 in sink
   * base cabinet" rather than "cabinet". Nothing is invented — an attribute
   * nobody stated is simply absent — and the text remains editable, because
   * the person searching always knows something the model does not.
   */
  async defaultQueryFor(
    materialDefinitionId: string,
    componentName: string | null,
    componentIds: readonly string[] = [],
  ): Promise<string> {
    const rows = await this.db.select<{ name: string }>(
      "SELECT name FROM material_definitions WHERE id = ?",
      [materialDefinitionId],
    );
    return buildSearchQuery({
      materialName: rows[0]?.name ?? "",
      componentName,
      attributes: await this.objectAttributes(componentIds),
    });
  }

  /** What the objects behind these components say they are. */
  private async objectAttributes(componentIds: readonly string[]): Promise<SearchAttribute[]> {
    if (componentIds.length === 0) return [];
    const placeholders = componentIds.map(() => "?").join(", ");
    const rows = await this.db.select<{
      definition_id: string;
      width: number;
      height_in: number | null;
      config: string | null;
    }>(
      `SELECT o.definition_id, o.width, o.height_in, o.config
         FROM assembly_instance_components c
         JOIN assembly_instances i ON i.id = c.instance_id
         JOIN plan_objects o ON o.feature_id = i.project_feature_id
        WHERE c.id IN (${placeholders}) AND o.archived_at IS NULL
        ORDER BY o.created_at LIMIT 8`,
      [...componentIds],
    );
    if (rows.length === 0) return [];
    // Several identical modules describe one need; several DIFFERENT ones
    // describe no single need, so the size is left out rather than guessed.
    const first = rows[0];
    const uniform = rows.every((r) => r.definition_id === first.definition_id && r.width === first.width);
    if (!uniform) return [];
    return objectSearchAttributes({
      definitionId: first.definition_id,
      width: Number(first.width),
      heightInches: first.height_in === null ? null : Number(first.height_in),
      config: first.config,
    });
  }

  /**
   * Which Home Depot store this project prices against: an explicit
   * per-project override, else the store serving the sourcing ZIP. The
   * provider defaults to an unrelated store when none is named, so this is
   * what makes "local pricing" actually local.
   */
  async storeForProject(projectId: string, zip: string): Promise<{ store: HomeDepotStore | null; match: "exact" | "nearby" | "override" | null }> {
    const rows = await this.db.select<{ sourcing_store_id: string | null }>(
      "SELECT sourcing_store_id FROM projects WHERE id = ?",
      [projectId],
    );
    const override = (rows[0]?.sourcing_store_id ?? "").trim();
    const { resolveStoreForZip, storeById } = await storeIndex();
    if (override !== "") {
      return { store: storeById(override) ?? { storeId: override, postalCode: "", address: "" }, match: "override" };
    }
    const resolved = resolveStoreForZip(zip);
    return resolved ? { store: resolved.store, match: resolved.match } : { store: null, match: null };
  }

  /* ---------------- cache ---------------- */

  private async readCache(key: string, ttlMs: number): Promise<{ json: unknown; fetchedAt: string } | null> {
    const rows = await this.db.select<CacheRow>(
      "SELECT response, fetched_at FROM provider_response_cache WHERE cache_key = ?",
      [key],
    );
    if (rows.length === 0) return null;
    const fetchedAt = rows[0].fetched_at;
    if (this.now().getTime() - new Date(fetchedAt).getTime() > ttlMs) return null;
    try {
      return { json: JSON.parse(rows[0].response), fetchedAt };
    } catch {
      return null;
    }
  }

  private async writeCache(key: string, engine: string, json: unknown): Promise<string> {
    const fetchedAt = this.now().toISOString();
    await this.db.execute(
      `INSERT INTO provider_response_cache (cache_key, provider_id, engine, response, fetched_at)
       VALUES (?, ?, ?, ?, ?)
       ON CONFLICT(cache_key) DO UPDATE SET response = excluded.response, fetched_at = excluded.fetched_at`,
      [key, HOME_DEPOT_PROVIDER_ID, engine, JSON.stringify(json), fetchedAt],
    );
    return fetchedAt;
  }

  /** Remove cache entries older than the TTL (housekeeping only). */
  async pruneCache(ttlMs: number = PROVIDER_CACHE_TTL_MS): Promise<void> {
    const cutoff = new Date(this.now().getTime() - ttlMs).toISOString();
    await this.db.execute("DELETE FROM provider_response_cache WHERE fetched_at < ?", [cutoff]);
  }

  /* ---------------- provider calls ---------------- */

  /**
   * Home Depot search (engine=home_depot) scoped to the project's sourcing
   * ZIP. Identical (query + ZIP + store + page) requests reuse the local
   * cache — reopening a requirement costs nothing.
   */
  async search(options: SearchOptions): Promise<SearchOutcome> {
    const query = options.query.trim();
    if (query === "") throw new ValidationError(["Enter something to search for."]);
    const resolved = options.projectId
      ? await this.storeForProject(options.projectId, options.zip)
      : { store: options.storeId ? (await storeIndex()).storeById(options.storeId) : null, match: options.storeId ? ("override" as const) : null };
    const params: Record<string, string> = { q: query, country: "us" };
    if (options.zip.trim() !== "") params.delivery_zip = options.zip.trim();
    const storeId = options.storeId ?? resolved.store?.storeId ?? null;
    if (storeId) params.store_id = storeId;
    if (options.page && options.page > 1) params.page = String(options.page);

    const key = providerCacheKey("home_depot", params);
    const finish = (json: unknown, fromCache: boolean, fetchedAt: string): SearchOutcome => {
      const context = parseSearchStoreContext(json);
      return {
        results: parseSearchResponse(json),
        fromCache,
        fetchedAt,
        query,
        zip: options.zip,
        storeId: context.storeId,
        storeName: context.storeName,
        requestedStore: resolved.store,
        requestedStoreMatch: resolved.match,
      };
    };
    if (!options.forceRefresh) {
      const cached = await this.readCache(key, PROVIDER_CACHE_TTL_MS);
      if (cached) return finish(cached.json, true, cached.fetchedAt);
    }
    const json = await this.executor.request("home_depot", params);
    const fetchedAt = await this.writeCache(key, "home_depot", json);
    return finish(json, false, fetchedAt);
  }

  /**
   * Home Depot product detail (engine=home_depot_product) — requested only
   * when the user expands or selects a result, never for every row.
   */
  async detail(productId: string, zip: string, storeId?: string | null, forceRefresh = false): Promise<DetailOutcome> {
    const params: Record<string, string> = { product_id: productId };
    if (zip.trim() !== "") params.delivery_zip = zip.trim();
    if (storeId) params.store_id = storeId;
    const key = providerCacheKey("home_depot_product", params);
    if (!forceRefresh) {
      const cached = await this.readCache(key, PROVIDER_CACHE_TTL_MS);
      if (cached) {
        const detail = parseProductResponse(cached.json);
        return { detail, coverage: coverageFor(detail), fromCache: true, fetchedAt: cached.fetchedAt };
      }
    }
    const json = await this.executor.request("home_depot_product", params);
    const fetchedAt = await this.writeCache(key, "home_depot_product", json);
    const detail = parseProductResponse(json);
    return { detail, coverage: coverageFor(detail), fromCache: false, fetchedAt };
  }

  /* ---------------- supplier / product identity ---------------- */

  private async homeDepotSupplierId(): Promise<string> {
    const rows = await this.db.select<{ id: string }>(
      "SELECT id FROM suppliers WHERE lower(name) = lower(?) ORDER BY created_at LIMIT 1",
      [HOME_DEPOT_SUPPLIER_NAME],
    );
    if (rows.length > 0) return rows[0].id;
    const supplier = await this.catalog.createSupplier({
      name: HOME_DEPOT_SUPPLIER_NAME,
      accountType: "retail",
      website: "https://www.homedepot.com",
      notes: "Created automatically for live product sourcing.",
    });
    return supplier.id;
  }

  /** Existing Product for this provider identity, if we have seen it. */
  async findByExternalIdentity(externalProductId: string): Promise<Product | null> {
    const rows = await this.db.select<{ id: string }>(
      "SELECT id FROM products WHERE external_provider_id = ? AND external_product_id = ?",
      [HOME_DEPOT_PROVIDER_ID, externalProductId],
    );
    return rows.length > 0 ? this.catalog.getProduct(rows[0].id) : null;
  }

  /**
   * Turn a chosen provider result into the project's selection:
   * Product (created or reused) → provider-provenanced Offer → project
   * snapshot → regenerated BOM/estimate. No manual typing of title, price,
   * SKU or URL anywhere in this path.
   */
  async selectResult(input: SelectResultInput): Promise<SelectResultOutcome> {
    if (input.componentIds.length === 0) {
      throw new ValidationError(["No requirement components to apply this product to."]);
    }
    const detail = input.detail ?? null;
    const result = input.result;
    const externalId = detail?.productId ?? result.productId;
    const title = displayProductName({ brand: detail?.brand ?? result.brand, title: detail?.title ?? result.title });
    const brand = detail?.brand ?? result.brand ?? "";
    const model = detail?.modelNumber ?? result.modelNumber ?? "";
    const sku = detail?.storeSku ?? model;
    const priceCents = detail?.priceCents ?? result.priceCents;
    const url = homeDepotProductUrl({ link: detail?.link ?? result.link, productId: externalId });
    const supplierId = await this.homeDepotSupplierId();

    // Coverage: provider figure → an existing product's figure → the user's
    // one missing field. Requirements measured in "each" need no package.
    const definition = await this.db.select<{ construction_unit: string }>(
      "SELECT construction_unit FROM material_definitions WHERE id = ?",
      [input.materialDefinitionId],
    );
    const constructionUnit = (definition[0]?.construction_unit ?? "each") as QuantityUnit;
    const coverage: CoverageFinding | null = input.coverage ?? null;
    const manual = input.manualCoverage ?? null;

    let product = await this.findByExternalIdentity(externalId);
    let productCreated = false;
    let missingCoverage = false;

    if (!product) {
      let coveragePerUnitX100 = 100;
      let coverageUnit: QuantityUnit = constructionUnit;
      let purchaseUnit: QuantityUnit = constructionUnit === "each" ? "each" : "case";
      let coverageSource = "provider";
      if (manual) {
        coveragePerUnitX100 = manual.coveragePerUnitX100;
        coverageUnit = manual.coverageUnit;
        purchaseUnit = manual.purchaseUnit;
        coverageSource = "manual";
      } else if (coverage) {
        coveragePerUnitX100 = coverage.coveragePerUnitX100;
        coverageUnit = coverage.coverageUnit;
        purchaseUnit = coverage.purchaseUnit;
      } else if (constructionUnit !== "each") {
        // The provider gave no package figure and the requirement is not
        // per-item: report it, ask for that ONE field, never re-entry.
        missingCoverage = true;
        coverageSource = "unknown";
      } else {
        coverageSource = "implied-each";
      }
      product = await this.catalog.createProduct({
        materialDefinitionId: input.materialDefinitionId,
        name: title,
        brand,
        sku,
        supplierId,
        purchaseUnit,
        coveragePerUnitX100,
        coverageUnit,
        productUrl: url ?? "",
        notes: detail?.upc ? `UPC ${detail.upc}` : "",
      });
      productCreated = true;
      await this.db.execute(
        "UPDATE products SET external_provider_id = ?, external_product_id = ?, coverage_source = ? WHERE id = ?",
        [HOME_DEPOT_PROVIDER_ID, externalId, coverageSource, product.id],
      );
      // Structured attributes from the detail response (provenance, and the
      // raw material for future compatibility rules).
      if (detail) {
        for (const spec of detail.specifications.slice(0, 40)) {
          await this.catalog.setAttribute(product.id, spec.name, spec.value).catch(() => undefined);
        }
        if (detail.upc) await this.catalog.setAttribute(product.id, "UPC", detail.upc).catch(() => undefined);
      }
    } else {
      // Reused: refresh only the identity/link fields the provider owns.
      await this.catalog.updateProduct(product.id, {
        materialDefinitionId: product.materialDefinitionId,
        name: product.name,
        brand: product.brand || brand,
        sku: product.sku || sku,
        supplierId: product.supplierId ?? supplierId,
        purchaseUnit: product.purchaseUnit,
        coveragePerUnitX100: product.coveragePerUnitX100,
        coverageUnit: product.coverageUnit,
        productUrl: product.productUrl || (url ?? ""),
        notes: product.notes,
      });
      if (manual) {
        await this.setProductCoverage(product.id, manual);
      } else if (coverage) {
        // Backfill provider package data onto a product whose coverage was
        // still outstanding. A figure the USER supplied is never overwritten.
        const src = await this.db.select<{ coverage_source: string | null }>(
          "SELECT coverage_source FROM products WHERE id = ?",
          [product.id],
        );
        const current = src[0]?.coverage_source ?? null;
        if (current === null || current === "unknown") {
          await this.setProductCoverage(product.id, coverage);
          await this.db.execute("UPDATE products SET coverage_source = 'provider' WHERE id = ?", [product.id]);
        }
      }
      const refreshed = await this.catalog.getProduct(product.id);
      if (refreshed) product = refreshed;
      missingCoverage = constructionUnit !== "each" && product.coveragePerUnitX100 <= 100 && !manual && !coverage;
    }

    // Provider-provenanced offer: this is live market data, labeled as such.
    const observedAt = this.now().toISOString().slice(0, 10);
    const offer = await this.catalog.addObservation(product.id, {
      supplierId,
      priceCents: priceCents ?? 0,
      priceIncludesTax: false,
      sourceType: "retail",
      observedAt,
      validUntil: null,
      contextNote: [
        "Home Depot",
        input.storeName ?? result.pickupStoreName ?? null,
        input.zip.trim() === "" ? null : `${input.zip.trim()} market`,
      ].filter(Boolean).join(" — "),
      notes: "",
      marketZip: input.zip.trim() || null,
      availability:
        result.pickupQuantity !== null && result.pickupQuantity > 0
          ? "in-stock"
          : result.pickupQuantity === 0
            ? "out-of-stock"
            : result.freeDelivery === true
              ? "in-stock"
              : "unknown",
      offerUrl: url ?? "",
      supplierSku: sku,
      providerId: HOME_DEPOT_PROVIDER_ID,
    });

    // Copy-on-use snapshot into the project, then regenerate the estimate.
    for (const componentId of input.componentIds) {
      await this.catalog.selectProduct(componentId, product.id, offer.id, { allowDiscontinued: true });
    }
    await this.assemblies.regenerateForProject(input.projectId);
    void this.bom; // regeneration prices through the BOM hook

    return { productId: product.id, productCreated, offerId: offer.id, missingCoverage, productUrl: url };
  }

  /** Supply ONLY the package figure the provider did not return. */
  async setProductCoverage(
    productId: string,
    coverage: { coveragePerUnitX100: number; coverageUnit: QuantityUnit; purchaseUnit: QuantityUnit },
  ): Promise<void> {
    const product = await this.catalog.getProduct(productId);
    if (!product) throw new ValidationError(["The product no longer exists."]);
    if (!Number.isSafeInteger(coverage.coveragePerUnitX100) || coverage.coveragePerUnitX100 <= 0) {
      throw new ValidationError(["Package coverage must be a positive number."]);
    }
    await this.catalog.updateProduct(productId, {
      materialDefinitionId: product.materialDefinitionId,
      name: product.name,
      brand: product.brand,
      sku: product.sku,
      supplierId: product.supplierId,
      purchaseUnit: coverage.purchaseUnit,
      coveragePerUnitX100: coverage.coveragePerUnitX100,
      coverageUnit: coverage.coverageUnit,
      productUrl: product.productUrl,
      notes: product.notes,
    });
    await this.db.execute("UPDATE products SET coverage_source = 'manual' WHERE id = ?", [productId]);
  }

  /** Re-price a product from the provider without touching any estimate. */
  async refreshOffer(productId: string, zip: string): Promise<{ priceCents: number | null }> {
    const rows = await this.db.select<{ external_product_id: string | null }>(
      "SELECT external_product_id FROM products WHERE id = ?",
      [productId],
    );
    const externalId = rows[0]?.external_product_id ?? null;
    if (!externalId) throw new ValidationError(["This product has no Home Depot identity to refresh."]);
    const { detail } = await this.detail(externalId, zip, null, true);
    if (!detail) throw new ProviderError("provider", "Home Depot did not return this product.");
    const supplierId = await this.homeDepotSupplierId();
    await this.catalog.addObservation(productId, {
      supplierId,
      priceCents: detail.priceCents ?? 0,
      priceIncludesTax: false,
      sourceType: "retail",
      observedAt: this.now().toISOString().slice(0, 10),
      validUntil: null,
      contextNote: zip.trim() === "" ? "Home Depot" : `Home Depot — ${zip.trim()} market`,
      notes: "",
      marketZip: zip.trim() || null,
      availability: "unknown",
      offerUrl: homeDepotProductUrl({ link: detail.link, productId: externalId }) ?? "",
      supplierSku: detail.storeSku ?? "",
      providerId: HOME_DEPOT_PROVIDER_ID,
    });
    // NOTE: deliberately does NOT update the project snapshot — Keep /
    // Update stays an explicit user decision (spec §26A).
    return { priceCents: detail.priceCents };
  }
}
